import { useEffect, useState, useMemo } from 'react';
import {
  TextInput,
  BadgeGroup,
  Button,
  Progress,
  Badge
} from '@burna/monster-design-system';
import { useLocation } from 'react-router-dom';
import useScanner from 'hooks/useScanner';
import useDownload from 'hooks/useDownload';
import cyberno from 'assets/cyberno.png';
import styles from './index.module.scss';

function Download({ isPasswordProtected = false }) {
  const location = useLocation();
  const scanner = useScanner({
    downloadPath: location.pathname,
    autoScan: true,
    noDetails: true
  });
  const [password, setPassword] = useState('');
  const { startDownload, loading, error, message, isLinkDownloaded, percent } =
    useDownload();

  const fileName = location.pathname.split('/').pop();

  const isScanning = useMemo(
    () =>
      !scanner.data || scanner.data.some(item => item.progress === 'loading'),
    [scanner.data]
  );
  const hasMalware = useMemo(
    () => scanner.data?.some(item => item.malwareState === 'malware'),
    [scanner.data]
  );

  const onSubmit = event => {
    event.preventDefault();
    startDownload({
      downloadPath: location.pathname,
      password: isPasswordProtected && password
    });
  };

  return (
    <section className={styles.container}>
      <form className={styles.form}>
        <p className={styles.tagline}>Download your file!</p>
        {scanner.outOfTime ? (
          <BadgeGroup
            className={styles.badgeGroup}
            badgePlacement="trailing"
            type="warning"
            icon="alert-circle"
            label="Time out"
            badgeLabel="Powered By"
            customIconUrl={cyberno}
          />
        ) : (
          <>
            <BadgeGroup
              className={styles.badgeGroup}
              badgePlacement="trailing"
              type={hasMalware ? 'error' : isScanning ? 'warning' : 'success'}
              label={
                hasMalware
                  ? 'File contains malware!'
                  : isScanning
                  ? 'File is scanning!'
                  : 'File successfully scanned!'
              }
              icon={isScanning ? 'loader:spin' : null}
              badgeLabel="Powered By"
              customIconUrl={cyberno}
            />
            <BadgeGroup
              className={styles.badgeGroupMobile}
              badgePlacement="trailing"
              type={hasMalware ? 'error' : isScanning ? 'warning' : 'success'}
              label={
                hasMalware
                  ? 'File contains malware!'
                  : isScanning
                  ? 'File is scanning!'
                  : 'File successfully scanned!'
              }
              icon={isScanning ? 'loader:spin' : null}
              badgeLabel="Powered By"
              customIconUrl={cyberno}
            />
          </>
        )}
        <TextInput className={styles.textInput} value={fileName} readOnly />
        {isPasswordProtected && (
          <TextInput
            className={styles.textInput}
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Enter password"
          />
        )}
        {percent !== -1 && (
          <Progress className={styles.progress} percent={percent} />
        )}
        <Button
          className={styles.submit}
          onClick={onSubmit}
          text="Download File"
          disabled={loading || (isPasswordProtected && !password)}
        />
        <Badge
          className={
            isLinkDownloaded || error ? styles.badge : styles.badgeHidden
          }
          size="sm"
          type={error ? 'error' : 'success'}
          label={message || 'x'}
        />
      </form>
    </section>
  );
}

export default Download;
